import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Legacy: still readable for a soft transition; new logins do NOT write here.
const LEGACY_TOKEN_KEY = "aximo_admin_token";

const AuthContext = createContext(null);

// Instance that always sends cookies (works same-origin and cross-origin with CORS credentials).
const api = axios.create({ baseURL: API, withCredentials: true });

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore network errors on logout */
        }
        localStorage.removeItem(LEGACY_TOKEN_KEY);
        setUser(null);
    }, []);

    // On mount: try session via cookie. If that fails, fall back to a legacy token
    // still in localStorage so pre-migration sessions don't get kicked out.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                if (!cancelled) setUser(data);
            } catch {
                const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
                if (legacy) {
                    try {
                        const { data } = await axios.get(`${API}/auth/me`, {
                            headers: { Authorization: `Bearer ${legacy}` },
                        });
                        if (!cancelled) setUser(data);
                    } catch {
                        localStorage.removeItem(LEGACY_TOKEN_KEY);
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        // Cookie is set by the server. We still cache the token in localStorage
        // as a fallback for legacy child pages that read `token` from context.
        localStorage.setItem(LEGACY_TOKEN_KEY, data.access_token);
        setUser({ email: data.email, role: "admin" });
        return data;
    };

    // Synthetic `token` value: truthy when authenticated so downstream pages
    // guarded by `if (!token) return` continue working while cookies do the real work.
    const token = user ? (localStorage.getItem(LEGACY_TOKEN_KEY) || "cookie") : null;

    return (
        <AuthContext.Provider value={{ token, user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
